import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Position } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { LoadingButton } from '#src/atoms/buttons/LoadingButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheck } from '#src/atoms/icons/system/iconCheck.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BrandM,
  HeadlineL,
  HeadlineM,
  LabelL,
  LabelM,
  LabelS,
} from '#src/atoms/typography/index.tsx';
import { AnimatedBackgroundBox } from '#src/atoms/ui/AnimatedBackgroundBox/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useFormatAnyPosition } from '#src/lib/players.ts';

import type { NoCardEntryComposeTeam_so5NoCardRouteFixturePickConfig } from './__generated__/index.graphql.ts';

const Root = styled.div`
  padding: var(--double-unit);
`;
const Lineup = styled(Vertical)`
  padding: var(--double-unit);
  text-align: center;
`;
const AppearanceContainer = styled.div`
  width: 150px;
  color: var(--c-nd-400);
  &.selected {
    color: var(--c-white);
  }
`;
const Substituted = styled(LabelM)`
  text-decoration: line-through;
`;
const Slots = styled(Horizontal).attrs({ center: true })`
  flex-wrap: wrap;
`;
const ModeContainer = styled(Vertical).attrs({ gap: 4, center: true })`
  margin: var(--double-unit);
`;
const Mode = styled(Vertical).attrs({ center: true })`
  position: relative;
  isolation: isolate;
  padding: var(--double-unit);
  width: 100%;
  height: 100%;
`;

type Appearance = {
  player?: {
    displayName: string;
  };
  team?: {
    name: string;
  } | null;
  season?: {
    startYear: number;
  } | null;
  bonus?: number | null;
  positions: Position[];
};

type AppearanceSlot = Appearance & {
  label?: string;
};

const formatAppearance = (
  appearance: Appearance | null | undefined,
  formatPosition: (position: Position) => string
) =>
  appearance?.player
    ? `${[
        appearance.player.displayName,
        appearance.team?.name,
        `(${appearance.positions.map(position => formatPosition(position)).join(', ')})`,
        appearance.season?.startYear,
      ]
        .filter(Boolean)
        .join(' - ')} (${appearance.bonus})`
    : '';

const Appearance = ({
  appearance,
  proposedAppearance,
}: {
  appearance: Appearance | null | undefined;
  proposedAppearance: Appearance | null | undefined;
}) => {
  const formatPosition = useFormatAnyPosition();

  return (
    <LabelL as="p">
      {proposedAppearance ? (
        <>
          <LabelM>
            {formatAppearance(proposedAppearance, formatPosition)}
          </LabelM>
          <Substituted>
            {formatAppearance(appearance, formatPosition)}
          </Substituted>
        </>
      ) : (
        <LabelM>{formatAppearance(appearance, formatPosition)}</LabelM>
      )}
    </LabelL>
  );
};

const isLineupReady = (lineup: (Appearance | null)[]) =>
  lineup.every(appearance => !!appearance?.player);

type Props = {
  requestAppearance: (configSignature: string) => Promise<{
    appearance: Appearance | null | undefined;
    eligibleSlot: number | null | undefined;
  }>;
  acceptAppearance: () => Promise<boolean>;
  resetAppearances: () => Promise<void>;
  onSubmit: () => void;
  onClose?: () => void;
  open: boolean;
  disclaimers: MessageDescriptor[];
  configs: NoCardEntryComposeTeam_so5NoCardRouteFixturePickConfig[];
};

export const ComposeTeam = ({
  configs,
  onSubmit,
  requestAppearance,
  acceptAppearance,
  resetAppearances,
  open,
  onClose,
  disclaimers,
}: Props) => {
  const [activeConfig, setActiveConfig] =
    useState<NoCardEntryComposeTeam_so5NoCardRouteFixturePickConfig | null>(
      configs.length === 1 ? configs[0] : null
    );
  const initialized = useRef(false);
  const [loading, setLoading] = useState(false);
  const [reseting, setReseting] = useState(true);
  const [proposedAppearance, setProposedAppearance] = useState<{
    appearance: Appearance | null;
    eligibleSlot: number;
  } | null>();
  const formatPosition = useFormatAnyPosition();
  const [lineup, setLineup] = useState<AppearanceSlot[]>(
    activeConfig?.pickingSlots || []
  );
  const ready = isLineupReady(lineup);

  const requestNewAppearance = useCallback(
    async (configSignature: string | undefined) => {
      setLoading(true);
      const { appearance, eligibleSlot } = await requestAppearance(
        configSignature!
      );
      if (appearance && typeof eligibleSlot === 'number') {
        setProposedAppearance({ appearance, eligibleSlot });
      }
      setLoading(false);
    },
    [requestAppearance]
  );

  const acceptProposedAppearance = async () => {
    const success = await acceptAppearance();

    if (!success) return;

    if (proposedAppearance?.appearance) {
      const newLineup = [...lineup];
      newLineup[proposedAppearance.eligibleSlot] = {
        ...newLineup[proposedAppearance.eligibleSlot],
        ...proposedAppearance.appearance,
      };
      setLineup(newLineup);
      setProposedAppearance(null);
      if (!isLineupReady(newLineup)) {
        requestNewAppearance(activeConfig?.signature);
      }
    }
  };

  const setConfig = (
    config: NoCardEntryComposeTeam_so5NoCardRouteFixturePickConfig
  ) => {
    setActiveConfig(config);
    setLineup(config.pickingSlots);
    requestNewAppearance(config.signature);
  };

  const onReset = async () => {
    setReseting(true);
    setProposedAppearance(null);
    await resetAppearances();
    setLineup(activeConfig?.pickingSlots || []);
    if (configs.length !== 1) {
      setActiveConfig(null);
    } else {
      requestNewAppearance(configs[0].signature);
    }
    setReseting(false);
  };

  const onResetEvent = useEffectEvent(onReset);

  const hasPlayersInLineup = lineup.some(appearance => appearance?.player);
  useEffect(() => {
    if (open && !initialized.current && !hasPlayersInLineup) {
      initialized.current = true;
      onResetEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasPlayersInLineup]);

  if (reseting) {
    return null;
  }

  if (!activeConfig) {
    return (
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={onClose}
        title={
          <HeadlineL>
            <FormattedMessage defaultMessage="Select a mode" />
          </HeadlineL>
        }
      >
        <Root>
          <ModeContainer gap={4} center>
            {configs.map((config, index) => (
              <AnimatedBackgroundBox
                primaryColor={
                  index % 2 === 0 ? 'var(--c-blue-400)' : 'var(--c-red-400)'
                }
                key={config.signature}
                onClick={() => setConfig(config)}
              >
                <Mode>
                  <BrandM>{config.name}</BrandM>
                </Mode>
              </AnimatedBackgroundBox>
            ))}
          </ModeContainer>
        </Root>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      title={
        <HeadlineL>
          <FormattedMessage defaultMessage="Validate your lineup" />
        </HeadlineL>
      }
    >
      <Root>
        <Vertical gap={4}>
          <Lineup gap={4}>
            <Slots>
              {lineup.map((appearance, index) => (
                <AppearanceContainer
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={classnames({
                    selected: proposedAppearance?.eligibleSlot === index,
                  })}
                >
                  <HeadlineM as="p">
                    {appearance.label ||
                      appearance.positions
                        .map(position => formatPosition(position))
                        .join(', ')}{' '}
                    {!!appearance.player && (
                      <FontAwesomeIcon icon={iconCheck} size="xs" />
                    )}
                  </HeadlineM>
                  <Appearance
                    appearance={appearance}
                    proposedAppearance={
                      proposedAppearance?.eligibleSlot === index
                        ? proposedAppearance.appearance!
                        : null
                    }
                  />
                </AppearanceContainer>
              ))}
            </Slots>
          </Lineup>
          <Vertical gap={4}>
            <Vertical gap={2}>
              <Horizontal>
                <Button
                  fullWidth
                  size="medium"
                  color="tertiary"
                  onClick={() => {
                    requestNewAppearance(activeConfig.signature);
                  }}
                  disabled={ready || loading}
                >
                  <FormattedMessage defaultMessage="Deny" />
                </Button>
                <LoadingButton
                  fullWidth
                  size="medium"
                  color="primary"
                  onClick={acceptProposedAppearance}
                  disabled={ready || loading}
                  loading={ready || loading}
                >
                  <FormattedMessage defaultMessage="Accept" />
                </LoadingButton>
              </Horizontal>
              <Button
                fullWidth
                color="tertiary"
                size="medium"
                onClick={onReset}
              >
                <FormattedMessage defaultMessage="Reset" />
              </Button>
            </Vertical>
            <Button
              fullWidth
              size="medium"
              onClick={onSubmit}
              disabled={!ready || loading}
            >
              <FormattedMessage defaultMessage="Submit" />
            </Button>
          </Vertical>
          <Vertical>
            {disclaimers.map(disclaimer => (
              <LabelS key={disclaimer.id} color="var(--c-nd-600)">
                <FormattedMessage {...disclaimer} />
              </LabelS>
            ))}
          </Vertical>
        </Vertical>
      </Root>
    </Dialog>
  );
};

ComposeTeam.fragments = {
  so5NoCardRouteFixturePickConfig: gql`
    fragment NoCardEntryComposeTeam_so5NoCardRouteFixturePickConfig on So5NoCardRouteFixturePickConfig {
      id
      name
      signature
      pickingSlots {
        positions
      }
    }
  ` as TypedDocumentNode<NoCardEntryComposeTeam_so5NoCardRouteFixturePickConfig>,
};
